// src/components/common/CookieSettings.js
import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTranslations } from '../../languages';
import { setConsent, hasStoredConsent } from '../../services/analyticsService';

// Styles for the cookie settings component
const styles = {
  container: {
    marginBottom: '40px',
    padding: '20px',
    backgroundColor: 'rgba(35, 35, 35, 0.7)',
    borderRadius: '8px',
    boxShadow: '0 5px 15px rgba(0,0,0,0.2)',
  },
  title: {
    fontSize: '1.5rem',
    marginBottom: '15px',
    color: '#4FCCED', // Using primary color
    fontWeight: 'bold',
  },
  description: {
    marginBottom: '20px',
    lineHeight: '1.6',
  },
  statusContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
  },
  statusLabel: {
    marginRight: '10px',
    fontWeight: 'bold',
  },
  statusValue: {
    padding: '4px 10px',
    borderRadius: '4px',
    fontWeight: 'bold',
  },
  statusEnabled: {
    backgroundColor: 'rgba(107, 94, 217, 0.2)',
    color: '#6B5ED9', // Accent color
  },
  statusDisabled: {
    backgroundColor: 'rgba(255, 70, 70, 0.2)',
    color: '#FF4646',
  },
  buttonsContainer: {
    display: 'flex',
    gap: '15px',
  },
  buttonAccept: {
    backgroundColor: '#6B5ED9', // Accent color
    color: '#EEF2F0',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
  },
  buttonDecline: {
    backgroundColor: 'transparent',
    color: '#6B5ED9', // Accent color
    border: '1px solid #6B5ED9',
    borderRadius: '4px',
    padding: '8px 16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
};

function CookieSettings() {
  const { language } = useLanguage();
  const translations = getTranslations(language);
  const [consentStatus, setConsentStatus] = useState(false);
  
  // Check the current consent status
  useEffect(() => {
    setConsentStatus(hasStoredConsent());
  }, []);
  
  // Translations
  const texts = {
    en: {
      title: "Analytics Cookie Settings",
      description: "You can change your preferences for analytics cookies at any time. We only use cookies for anonymous usage statistics if you accept them.",
      status: "Current status:",
      enabled: "Enabled",
      disabled: "Disabled",
      accept: "Accept Analytics",
      decline: "Disable Analytics"
    },
    de: {
      title: "Analytics-Cookie-Einstellungen",
      description: "Sie können Ihre Präferenzen für Analytics-Cookies jederzeit ändern. Wir verwenden Cookies nur für anonyme Nutzungsstatistiken, wenn Sie diese akzeptieren.",
      status: "Aktueller Status:",
      enabled: "Aktiviert",
      disabled: "Deaktiviert",
      accept: "Analytics akzeptieren",
      decline: "Analytics deaktivieren"
    }
  };
  
  const currentTexts = language === 'de' ? texts.de : texts.en;
  
  // Handle accept cookies
  const handleAccept = () => {
    setConsent(true);
    setConsentStatus(true);
    
    // Reload the page to ensure analytics is loaded
    // This is optional, but ensures a clean state
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };
  
  // Handle decline cookies
  const handleDecline = () => {
    setConsent(false);
    setConsentStatus(false);
    
    // Reload the page to ensure analytics is removed
    // This is optional, but ensures a clean state
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };
  
  return (
    <div style={styles.container}>
      <h3 style={styles.title}>{currentTexts.title}</h3>
      <p style={styles.description}>{currentTexts.description}</p>
      
      <div style={styles.statusContainer}>
        <span style={styles.statusLabel}>{currentTexts.status}</span>
        {consentStatus ? (
          <span style={{...styles.statusValue, ...styles.statusEnabled}}>
            {currentTexts.enabled}
          </span>
        ) : (
          <span style={{...styles.statusValue, ...styles.statusDisabled}}>
            {currentTexts.disabled}
          </span>
        )}
      </div>
      
      <div style={styles.buttonsContainer}>
        {!consentStatus && (
          <button 
            style={styles.buttonAccept}
            onClick={handleAccept}
          >
            {currentTexts.accept}
          </button>
        )}
        
        {consentStatus && (
          <button 
            style={styles.buttonDecline}
            onClick={handleDecline}
          >
            {currentTexts.decline}
          </button>
        )}
      </div>
    </div>
  );
}

export default CookieSettings;